const OIL_LIMIT = 3

function normalized(value) {
  return String(value || '').trim().toLocaleLowerCase()
}

function classificationText(item) {
  return [
    item?.product_class,
    item?.type_bucket,
    item?.concentration,
    ...(Array.isArray(item?.concentrations) ? item.concentrations : []),
    item?.name,
  ].map(normalized).filter(Boolean).join(' ')
}

export function isPheromoneItem(item) {
  return /(^|[^a-z])pheromone([^a-z]|$)/i.test(classificationText(item))
}

export function isOilItem(item) {
  return /(^|[^a-z])(?:perfume\s+)?oil([^a-z]|$)/i.test(classificationText(item))
}

export function collectionBindingKey(item) {
  if (!item?.id || !item?.user_id) return ''
  return `${item.user_id}:${item.id}`
}

export function isEligibleCollectionItem(item) {
  return collectionBindingKey(item)
    && !item.deleted_at
    && !item.given_away
    && !item.finished
    && !item.finished_at
    && !isPheromoneItem(item)
}

export function eligibleOwnedOils(collection = [], anchorId = '') {
  return collection
    .filter(item => isEligibleCollectionItem(item) && item.id !== anchorId && isOilItem(item))
    .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')))
}

export function recommendationPage(recommendations = [], offset = 0, pageSize = 5) {
  const list = Array.isArray(recommendations) ? recommendations : []
  const safeOffset = Math.max(0, Math.trunc(Number(offset) || 0))
  const safeSize = Math.max(1, Math.trunc(Number(pageSize) || 5))
  const items = list.slice(safeOffset, safeOffset + safeSize)
  return {
    items,
    offset: safeOffset,
    hasMore: safeOffset + items.length < list.length,
    exhausted: list.length > 0 && safeOffset + items.length >= list.length,
  }
}

export function collectionItemForRecommendation(recommendation, collection = [], anchorId = '') {
  const eligible = collection.filter(item => isEligibleCollectionItem(item) && item.id !== anchorId)
  const catalogId = recommendation?.catalog_id ? String(recommendation.catalog_id) : ''
  const catalogMatch = catalogId
    ? eligible.find(item => item?.catalog_id && String(item.catalog_id) === catalogId)
    : null
  if (catalogMatch) return catalogMatch

  const name = String(recommendation?.name || '').trim()
  const house = String(recommendation?.house || '').trim()
  if (!name || !house) return null
  return resolveLayerLabels([`${house} - ${name}`], eligible)[0] || null
}

function suppliedRecommendationRank(recommendation) {
  const rank = Number(recommendation?.rank)
  return Number.isFinite(rank) && rank > 0 ? rank : null
}

export function orderLayeringRecommendations(recommendations = []) {
  const ordered = Array.isArray(recommendations) ? [...recommendations] : []
  if (!ordered.some(recommendation => suppliedRecommendationRank(recommendation) !== null)) return ordered

  return ordered
    .map((recommendation, index) => ({ recommendation, index, rank: suppliedRecommendationRank(recommendation) }))
    .sort((left, right) => {
      if (left.rank === null && right.rank === null) return left.index - right.index
      if (left.rank === null) return 1
      if (right.rank === null) return -1
      return left.rank - right.rank || left.index - right.index
    })
    .map(item => item.recommendation)
}

export function layeringRecommendationRank(recommendation, fallbackRank) {
  return suppliedRecommendationRank(recommendation) ?? fallbackRank
}

function wearTimestamp(log) {
  const value = log?.applications?.[0]?.applied_at || log?.created_at || log?.worn_on
  const timestamp = value ? new Date(value).getTime() : 0
  return Number.isFinite(timestamp) ? timestamp : 0
}

function wearSignals(logs, fragranceId) {
  const matches = (logs || []).filter(log => log?.fragrance_id === fragranceId)
  const ratings = matches
    .map(log => Number(log.rating ?? log.user_rating ?? log.wear_rating))
    .filter(value => Number.isFinite(value) && value > 0)
  return {
    count: matches.length,
    averageRating: ratings.length
      ? ratings.reduce((sum, value) => sum + value, 0) / ratings.length
      : 0,
    latest: matches.reduce((latest, log) => Math.max(latest, wearTimestamp(log)), 0),
  }
}

function expandConcentrations(collection = []) {
  const expanded = []
  for (const item of collection) {
    const concentrations = Array.isArray(item.concentrations) && item.concentrations.length > 0
      ? item.concentrations
      : [item.concentration || '']

    for (const concentration of concentrations) {
      expanded.push({
        ...item,
        concentration,
        _originalItem: item,
      })
    }
  }
  return expanded
}

export function rankLayeringCandidates({ collection = [], logs = [], anchorId = '', query = '' } = {}) {
  const cleanQuery = normalized(query)
  const expanded = expandConcentrations(collection)

  return expanded
    .filter(item => isEligibleCollectionItem(item._originalItem || item))
    .filter(item => item.id !== anchorId)
    .filter(item => {
      if (!cleanQuery) return true
      const searchText = normalized(`${item.house} ${item.name} ${item.concentration}`)
      const houseText = normalized(item.house)
      return searchText.includes(cleanQuery) || houseText.includes(cleanQuery)
    })
    .map(item => ({
      item,
      ownRating: Number(item.rob_rating) || 0,
      wear: wearSignals(logs, item.id),
      isOil: isOilItem(item),
    }))
    .sort((left, right) => {
      const leftOilRank = left.isOil ? 2 : 0
      const rightOilRank = right.isOil ? 2 : 0
      return leftOilRank - rightOilRank
        || right.ownRating - left.ownRating
        || right.wear.averageRating - left.wear.averageRating
        || right.wear.count - left.wear.count
        || right.wear.latest - left.wear.latest
        || String(left.item.name || '').localeCompare(String(right.item.name || ''))
        || String(left.item.concentration || '').localeCompare(String(right.item.concentration || ''))
    })
}

export function oilCount(items = []) {
  return items.filter(isOilItem).length
}

export function enforceOilLimit(items = [], anchor = null) {
  let oils = isOilItem(anchor) ? 1 : 0
  return items.filter(item => {
    if (!isOilItem(item)) return true
    if (oils >= OIL_LIMIT) return false
    oils += 1
    return true
  })
}

export function layerOptionState({ option, anchor, selected = [] } = {}) {
  const alreadySelected = selected.some(item => item.id === option?.id)
  const oilsInSet = oilCount([anchor, ...selected])
  const oilLimitReached = !alreadySelected && isOilItem(option) && oilsInSet >= OIL_LIMIT
  return {
    selected: alreadySelected,
    disabled: oilLimitReached,
    reason: oilLimitReached ? 'Layer sets can include up to 3 oils.' : '',
  }
}

export function toggleLayerOptionSelection({ option, anchor, selected = [] } = {}) {
  const state = layerOptionState({ option, anchor, selected })
  if (state.disabled) return selected
  return state.selected
    ? selected.filter(item => item.id !== option?.id)
    : [...selected, option]
}

export function resolveLayerLabels(labels = [], collection = []) {
  const byLabel = new Map(collection
    .filter(item => collectionBindingKey(item) && !isPheromoneItem(item))
    .map(item => [normalized(`${item.house} - ${item.name}`), item]))
  return labels.map(label => byLabel.get(normalized(label))).filter(Boolean)
}

export function serializeLayerRows(items = []) {
  return items.map(item => `${item.house} - ${item.name}`)
}

export function layeringUiText(value) {
  const retiredTerm = ['ba', 'se'].join('')
  return String(value || '').replace(new RegExp(`\\b${retiredTerm}\\b`, 'gi'), match => match === match.toLocaleUpperCase() ? 'ANCHOR' : match[0] === match[0].toLocaleUpperCase() ? 'Anchor' : 'anchor')
}

export function outOfCollectionRecommendations(scoredRecommendations = [], collection = []) {
  const ownedCatalogIds = new Set(collection.map(item => item?.catalog_id).filter(Boolean).map(String))
  const ownedIdentities = new Set(collection.map(item => normalized(`${item?.house}::${item?.name}`)))
  return scoredRecommendations.filter(({ recommendation }) => {
    const catalogId = recommendation?.catalog_id ? String(recommendation.catalog_id) : ''
    const identity = normalized(`${recommendation?.house}::${recommendation?.name}`)
    return !(catalogId && ownedCatalogIds.has(catalogId)) && !ownedIdentities.has(identity)
  })
}

export function layeringPickerMode({ loading = false, renderablePairings = [] } = {}) {
  if (loading) return 'loading'
  return renderablePairings.length > 0 ? 'pairings' : 'collection'
}

export const MAX_LAYER_OILS = OIL_LIMIT
